import React, { useEffect } from 'react';
import { MediaQueryProvider } from 'react-media-query-hoc';
import getConfig from 'next/config';
import { GetServerSideProps } from 'next';
import { ApolloProvider, gql, useQuery } from '@apollo/client';
import { getDataFromTree } from '@apollo/client/react/ssr';
import SkipLink from '@domain-group/fe-co-skip-link';
import { tokens } from '@domain-group/fe-brary';
import {
  AgencyHeroWithFragment,
  AgencyTopSummaryWithFragment,
  AgencyAboutSectionWithFragment,
  AgencyTeamWithFragment,
  AgencyRecommendationsWithFragment,
  AgencyVideoWithFragment,
  AgencyProfileMapWithFragment,
  AgencyCurrentListings,
  AgencyListingsStoryWithFragment,
  AgencySalesStatisticsStoryWithFragment,
  AgencySalesStatisticsWithFragment,
  AgencyEnquiryFormWithFragment,
  AgencyHeadWithQuery,
  ErrorBoundary,
  SectionWrapper,
  LoadingPage,
  AgencyProfileHeaderWithFragment,
  AgencyProfileFooterWithFragment,
} from '../../components';
import { ReviewsProvider } from '../../components/recommendations-enquiry-section';
import { ShortlistProvider } from '../../contexts';
import { skipLinkName } from '../../helper/accessibility-utils';
import { addApolloState, initializeApollo } from '../../lib/apollo';
import { getDomainWinstonLogger } from '../../core/logger';
import { COMPONENT_IDS, AGENCY_AGENT_LAYOUT_MAX_WIDTH } from '../../constants';
import {
  trackAgencyAboutSection,
  trackAgencyHero,
  trackAgencyTeamSection,
  trackCurrentListings,
  trackSalesStats,
  trackProfilePage,
  trackAgencyEnquiryForm,
  trackAgencyRecommendations,
} from '../../tracking';
import { getUserDetails, idFromSlug } from '../../utils';
import { toCanonicalAgencySlug } from '../../routes/utils';
import { version } from '../../../package.json';
import {
  isAgencyWithoutAppraisalProcess,
  isHideStarRatingsEnabled,
  isWebpImageFormatEnabled,
} from '../../services/feature-flag-services';
import * as styles from '../../styles/trade-profile.style';
import { isStarRatingDesignApplicable } from '../../utils/recommendations';
import { AgencyRecommendationsWithStarRatings } from './recommendations-with-star-ratings';
import { AgencyRecommendationsWithoutStarRatings } from './recommendations-without-star-ratings';

const { publicRuntimeConfig } = getConfig();

const NOT_ENOUGH_DATA_ERROR =
  'Insufficient data to render Agency Profile page.';
const ARCHIVED_ERROR = 'Agency is archived.';

export const AGENCY_PROFILE_QUERY = gql`
  query getAgencyProfile($agencyId: Int!, $imageFormat: ImageFormat) {
    agency(agencyId: $agencyId) {
      id
      agencyId
      profileUrl
      isArchived
      profileTier
      depthTier
      address {
        suburb {
          slug
        }
      }

      recommendations {
        numberOfReviews
        overallStarRating
      }

      ...${AgencyProfileHeaderWithFragment.fragmentName}
      ...${AgencyProfileFooterWithFragment.fragmentName}
      ...${AgencyHeroWithFragment.fragmentName}
      ...${AgencyTopSummaryWithFragment.fragmentName}
      ...${AgencyAboutSectionWithFragment.fragmentName}
      ...${AgencyTeamWithFragment.fragmentName}
      ...${AgencySalesStatisticsStoryWithFragment.fragmentName}
      ...${AgencySalesStatisticsWithFragment.fragmentName}
      ...${AgencyListingsStoryWithFragment.fragmentName}
      ...${AgencyRecommendationsWithFragment.fragmentName}
      ...${AgencyVideoWithFragment.fragmentName}
      ...${AgencyEnquiryFormWithFragment.fragmentName}
      ...${AgencyProfileMapWithFragment.fragmentName}
    }
  }

  ${AgencyProfileHeaderWithFragment.fragment}
  ${AgencyProfileFooterWithFragment.fragment}
  ${AgencyHeroWithFragment.fragment}
  ${AgencyTopSummaryWithFragment.fragment}
  ${AgencyAboutSectionWithFragment.fragment}
  ${AgencyTeamWithFragment.fragment}
  ${AgencySalesStatisticsStoryWithFragment.fragment}
  ${AgencySalesStatisticsWithFragment.fragment}
  ${AgencyListingsStoryWithFragment.fragment}
  ${AgencyRecommendationsWithFragment.fragment}
  ${AgencyVideoWithFragment.fragment}
  ${AgencyEnquiryFormWithFragment.fragment}
  ${AgencyProfileMapWithFragment.fragment}
`;

interface AgencyProfilePageProps {
  agencyId: number;
  isEmbeddedApp?: boolean;
  user?: {
    userId: string;
    sessionId: string;
    disableUserTracking: boolean;
    userName: string;
    givenName: string;
    familyName: string;
    postcode: string;
    userToken: string;
    emailHash: string;
    ipHash: string;
  };
  hideAppraisalCta?: boolean;
  starRatingsEnabled?: boolean;
  mixpanelHasInitialized?: boolean;
  hideStarRatings?: boolean;
  imageFormat?: string;
  quality?: number;
}

const AgencyProfilePage = ({
  isEmbeddedApp = false,
  agencyId,
  user,
  hideAppraisalCta = false,
  mixpanelHasInitialized = false,
  starRatingsEnabled = false,
  hideStarRatings = false,
  imageFormat,
  quality,
}: AgencyProfilePageProps): JSX.Element => {
  const { data, loading, error } = useQuery(AGENCY_PROFILE_QUERY, {
    variables: { agencyId, imageFormat },
    errorPolicy: 'all', // https://www.apollographql.com/docs/react/v2/data/error-handling/#error-policies
  });

  const hasReviews =
    // just to guard when there are reviews but numberOfReviews from RMA is somehow undefined
    data?.agency?.recommendations?.numberOfReviews !== undefined &&
    Boolean(data?.agency?.recommendations?.reviews?.length);

  const showRating = starRatingsEnabled && hasReviews;
  const hasOverallStarRating = Boolean(
    data?.agency?.recommendations?.overallStarRating,
  );
  const hasTotalReviews = Boolean(
    data?.agency?.recommendations?.numberOfReviews,
  );
  const overallStarRating =
    showRating && hasOverallStarRating && hasTotalReviews && !hideStarRatings
      ? {
          rating: data?.agency?.recommendations?.overallStarRating,
          totalReviews: data?.agency?.recommendations?.numberOfReviews,
        }
      : undefined;

  useEffect(() => {
    if (mixpanelHasInitialized && data?.agency) {
      trackProfilePage.trackPageViewAgencyProfile({
        locationId: data?.agency?.address?.suburb?.suburbSlug,
      });
    }
  }, [mixpanelHasInitialized, data?.agency, agencyId]);

  if (error) {
    throw new Error(`Something went wrong, ${error}`);
  }

  if (loading) {
    return <LoadingPage />;
  }

  if (!data || !agencyId) {
    throw new Error(NOT_ENOUGH_DATA_ERROR);
  }

  // If agency is archived, throw error to be handled in getServerSideProps
  if (data.agency?.isArchived) {
    throw new Error(ARCHIVED_ERROR);
  }

  const recommendationsOnSubmit = () =>
    trackAgencyEnquiryForm.trackSubmit({
      locationId: data?.agency?.address?.suburb?.suburbSlug,
    });

  return (
    <ErrorBoundary>
      <>
        <AgencyHeadWithQuery
          agencyId={agencyId}
          user={user}
          imageFormat={imageFormat}
        />
        <SkipLink anchorName={skipLinkName} />
        <div
          css={styles.page}
          data-testid="trade-profile"
          data-version={version}
        >
          <MediaQueryProvider>
            <ShortlistProvider>
              <ReviewsProvider>
                <AgencyHeroWithFragment
                  agencyId={agencyId}
                  renderComponent={
                    isEmbeddedApp ? null : (
                      <AgencyProfileHeaderWithFragment agencyId={agencyId} />
                    )
                  }
                  emailFormComponentId={COMPONENT_IDS.enquiryForm}
                  onAgencyAddressClick={trackAgencyHero.agencyAddressClick}
                  onAgencyLogoClick={trackAgencyHero.agencyLogoClick}
                  onCallButtonClick={trackAgencyHero.callButtonClick}
                  onEmailButtonClick={trackAgencyHero.emailButtonClick}
                  onSocialClick={trackAgencyHero.trackSocialLink}
                  onVendorTotalReviewsClick={
                    trackAgencyRecommendations.trackAgencyTotalReview
                  }
                  desktopWidth={AGENCY_AGENT_LAYOUT_MAX_WIDTH}
                  overallStarRating={overallStarRating}
                  imageFormat={imageFormat}
                />
                <SectionWrapper isShallow componentId={COMPONENT_IDS.summary}>
                  <AgencyTopSummaryWithFragment
                    agencyId={agencyId}
                    overallStarRating={overallStarRating}
                    profileTier={data?.agency?.profileTier}
                  />
                </SectionWrapper>
                <SectionWrapper
                  isShallow
                  isNarrow
                  componentId={COMPONENT_IDS.about}
                >
                  <AgencyAboutSectionWithFragment
                    agencyId={agencyId}
                    onReadMore={trackAgencyAboutSection.trackReadMore}
                  />
                </SectionWrapper>
                <SectionWrapper componentId={COMPONENT_IDS.team}>
                  <AgencyTeamWithFragment
                    agencyId={agencyId}
                    hideAppraisalCta={hideAppraisalCta}
                    onViewMore={trackAgencyTeamSection.trackViewMore}
                    onAgentCall={trackAgencyTeamSection.trackCall}
                    onAgentCallReveal={trackAgencyTeamSection.trackCallReveal}
                    onAgentGetAppraisal={
                      trackAgencyTeamSection.trackGetAppraisal
                    }
                    onAgentOpenEnquiryForm={
                      trackAgencyTeamSection.trackOpenEnquiryForm
                    }
                    onAgentEnquirySubmit={
                      trackAgencyTeamSection.trackEnquirySubmit
                    }
                    onAgentEnquirySubmitSuccess={
                      trackAgencyTeamSection.trackEnquirySubmitSuccess
                    }
                    onAgentEnquirySubmitError={
                      trackAgencyTeamSection.trackEnquirySubmitError
                    }
                    mobilePadding={tokens.spacing.m}
                    starRatingsEnabled={starRatingsEnabled}
                    imageFormat={imageFormat}
                  />
                </SectionWrapper>
                <SectionWrapper componentId={COMPONENT_IDS.statistics}>
                  <AgencySalesStatisticsStoryWithFragment agencyId={agencyId} />
                  <AgencySalesStatisticsWithFragment
                    agencyId={agencyId}
                    tracking={{
                      onSuburbChange: trackSalesStats.agency.onSelectSuburb,
                    }}
                  />
                </SectionWrapper>
                <SectionWrapper componentId={COMPONENT_IDS.currentListings}>
                  <AgencyListingsStoryWithFragment agencyId={agencyId} />
                  <AgencyCurrentListings
                    agencyId={agencyId}
                    tracking={{
                      onSelectListingStatus:
                        trackCurrentListings.agency.onSelectListingStatus,
                      onSelectSelectSortBy:
                        trackCurrentListings.agency.onSortBy,
                      onSelectPropertyType:
                        trackCurrentListings.agency.onPropertyTypeChange,
                      onSelectSuburb:
                        trackCurrentListings.agency.onSuburbChange,
                      onSelectBedrooms:
                        trackCurrentListings.agency.onBedroomChange,
                      onPageChange: trackCurrentListings.agency.onViewMore,
                      onListingCardClick:
                        trackCurrentListings.agency.onListingCardClick,
                    }}
                    mobilePadding={tokens.spacing.m} // Removes the margin for the picker on mobile
                    imageFormat={imageFormat}
                    quality={quality}
                  />
                </SectionWrapper>
                {starRatingsEnabled ? (
                  <AgencyRecommendationsWithStarRatings
                    title={data?.agency?.name || ''}
                    agencyId={agencyId}
                    overallStarRating={overallStarRating}
                    starRatingsEnabled={starRatingsEnabled}
                    profileTier={data?.agency?.profileTier}
                    onActivate={trackAgencyEnquiryForm.trackEnquiryFormViewed}
                    onSubmit={recommendationsOnSubmit}
                  />
                ) : (
                  <AgencyRecommendationsWithoutStarRatings
                    agencyId={agencyId}
                    starRatingsEnabled={starRatingsEnabled}
                    onActivate={trackAgencyEnquiryForm.trackEnquiryFormViewed}
                    onSubmit={recommendationsOnSubmit}
                  />
                )}
                <SectionWrapper componentId={COMPONENT_IDS.video}>
                  <AgencyVideoWithFragment agencyId={agencyId} />
                </SectionWrapper>
                <SectionWrapper noStyles componentId={COMPONENT_IDS.map}>
                  <AgencyProfileMapWithFragment
                    agencyId={agencyId}
                    imageFormat={imageFormat}
                  />
                </SectionWrapper>
              </ReviewsProvider>
            </ShortlistProvider>
          </MediaQueryProvider>
        </div>
        {isEmbeddedApp ? null : (
          <AgencyProfileFooterWithFragment agencyId={agencyId} />
        )}
      </>
    </ErrorBoundary>
  );
};

export default AgencyProfilePage;

export const getServerSideProps: GetServerSideProps = async ({
  req,
  res,
  query,
}) => {
  const logger = getDomainWinstonLogger(__filename);
  const user = getUserDetails(req.user);

  // For GraphQL Query Data Requirements
  const agencySlug = query.searchParam;

  if (typeof agencySlug !== 'string') {
    return {
      redirect: { destination: '/real-estate-agents/', permanent: true },
    };
  }
  const agencyId = idFromSlug(agencySlug);

  if (!agencyId) {
    return {
      redirect: { destination: '/real-estate-agents/', permanent: true },
    };
  }

  const webpEnabled = await isWebpImageFormatEnabled(
    res.locals?.DEVICE_SESSIONID,
  );
  const imageFormat = webpEnabled ? 'WEBP' : undefined;
  const quality = webpEnabled ? 85 : undefined;

  const props = {
    // _app.tsx props
    serverInfo: res.locals.serverInfo,
    mixpanelToken: publicRuntimeConfig.MIXPANEL_TOKEN,

    isEmbeddedApp: req.isEmbeddedApp,

    // User data
    user,

    // Page props
    agencyId,
    imageFormat,
    quality,
  };

  const pageProps = JSON.parse(
    JSON.stringify({
      props,
    }),
  );

  const apolloClient = initializeApollo();

  try {
    await getDataFromTree(
      <ApolloProvider client={apolloClient}>
        <AgencyProfilePage {...pageProps.props} />
      </ApolloProvider>,
    );

    const { agency } = apolloClient.readQuery({
      query: AGENCY_PROFILE_QUERY,
      variables: {
        agencyId,
        imageFormat,
      },
    });

    if (!agency) {
      return {
        redirect: { destination: '/real-estate-agents/', permanent: true },
      };
    }

    // redirect to correct SEO url if there is a mismatch
    // Express normalises slugs to lowercase, so compare and redirect using
    // the lowercased profileUrl to avoid an infinite redirect loop where
    // SSR sends the browser to the mixed-case GQL profileUrl and Express
    // immediately lowercases it again.
    if (
      agency.profileUrl &&
      toCanonicalAgencySlug(agency.profileUrl) !==
        toCanonicalAgencySlug(agencySlug)
    ) {
      return {
        redirect: {
          destination: `/real-estate-agencies/${toCanonicalAgencySlug(agency.profileUrl)}`,
          permanent: true,
        },
      };
    }

    const starRatingsEnabled = isStarRatingDesignApplicable({
      profileTier: agency.profileTier,
      depthTier: agency.depthTier,
    });

    const [hideStarRatings, hideAppraisalCta] = await Promise.all([
      starRatingsEnabled
        ? isHideStarRatingsEnabled(agencyId)
        : // If ineligible for star ratings, default to hiding stars as well
          Promise.resolve(true),
      isAgencyWithoutAppraisalProcess(agencyId),
    ]);

    pageProps.props = {
      ...pageProps.props,
      starRatingsEnabled,
      hideAppraisalCta,
      hideStarRatings,
    };

    return addApolloState(apolloClient, pageProps);
  } catch (error: any) {
    logger.error('GraphQL SSR error\n', error);

    if (error?.message?.includes?.(NOT_ENOUGH_DATA_ERROR)) {
      return {
        redirect: { destination: '/real-estate-agents/', permanent: true },
      };
    }

    // Handle archived agencies
    if (error?.message?.includes?.(ARCHIVED_ERROR)) {
      const { agency } = apolloClient.readQuery({
        query: AGENCY_PROFILE_QUERY,
        variables: {
          agencyId,
          imageFormat,
        },
      });
      return {
        redirect: {
          destination: agency?.address?.suburb?.slug
            ? `/real-estate-agencies/${agency.address.suburb.slug}`
            : '/real-estate-agents',
          permanent: true,
        },
      };
    }

    throw new Error('Agency Profile SSR error', error);
  }
};
